import moment from 'moment';

import { getCompanyABTs } from '../../../reducers/company/resources';

/** One WhatsApp sender (botmap) as returned by GET /api/v3/company/{id}/whatsapp. */
export interface Sender {
  botmapId: string;
  botId: string;
  displayName: string;
  sender: string;
  wabaId: string | null;
  status: string;
  startModuleId: string | null;
  assignedUsers: number;
  entryPoint: string;
  createdAt?: string;
  removedAt?: string | null;
}

export interface SenderEvent {
  id: string;
  botmapId: string;
  action: 'registered' | 'updated' | 'removed' | 'moved_out' | 'moved_in' | 'user_added' | 'user_removed'
    | 'templates_synced' | 'template_mapped' | string;
  details: Record<string, any>;
  actorName: string | null;
  createdAt: string;
}

export interface SenderUser {
  id: string;
  name: string;
  email: string;
  role: string;
  onSender: boolean;
}

export interface AbtBotOption {
  value: string;
  label: string;
}

export const SENDER_STATUSES = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

/** backend-v2 wraps /api/* 200s in { statusCode, data }. */
export const unwrap = (res: any): any => (
  res && typeof res === 'object' && 'statusCode' in res && 'data' in res ? res.data : res
);

/** apiCore rethrows the error body ({ statusCode, error, message }). */
export const apiError = (err: any, fallback: string): string => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  return (err.response && err.response.data && err.response.data.message) || err.message || fallback;
};

export const formatSender = (digits: string): string => {
  const d = String(digits || '').replace(/[^0-9]/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return d ? `+${d}` : '';
};

export const statusColor = (status: string): string => {
  const s = String(status || '').toUpperCase();
  if (s === 'ACTIVE') return 'green';
  if (s === 'PENDING' || s === 'PENDING_VERIFICATION') return 'gold';
  if (s === 'SUSPENDED' || s === 'DISABLED') return 'red';
  return 'default';
};

export const statusLabel = (status: string): string => {
  const s = String(status || '').toUpperCase();
  const known = SENDER_STATUSES.find((o) => o.value === s);
  if (known) return known.label;
  return s ? s.charAt(0) + s.slice(1).toLowerCase() : '';
};

/** "Oct 7, 2026 3:06 AM" */
export const formatDateTime = (value?: string | null): string => (value ? moment(value).format('MMM D, YYYY h:mm A') : '');

/** "Oct 7, 2026" */
export const formatDate = (value?: string | null): string => (value ? moment(value).format('MMM D, YYYY') : '');

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/**
 * The company's Apply-By-Text bots as Select options. `excludeBotIds` drops the
 * WhatsApp sender's own bot, which the ABT endpoint also lists.
 */
export async function loadAbtBotOptions(companyId: string, excludeBotIds: string[] = []): Promise<AbtBotOption[]> {
  if (!companyId) return [];
  try {
    const rows = unwrap(await getCompanyABTs(companyId));
    const skip = new Set(excludeBotIds.filter(Boolean));
    return (Array.isArray(rows) ? rows : [])
      .filter((b: any) => b && b.id && b.name && b.type !== 'whatsapp' && !skip.has(b.id))
      .map((b: any) => ({ value: b.id, label: b.mobile ? `${b.name} (${formatSender(b.mobile)})` : b.name }));
  } catch {
    return [];
  }
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
